import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHead,
  TableRow,
  TableHeaderRow
} from './ui/table';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from './ui/pagination';
import { Checkbox } from './ui/checkbox';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from './ui/tooltip';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

export function ShadcnDataTable({
  columns,
  data,
  onRowClick,
  selectable,
  selected = [],
  onSelectAll,
  onSelectOne,
  actions,
  page = 0,
  rowsPerPage = 10,
  totalCount = 0,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
}) {
  const { t } = useTranslation();
  const [localPage, setLocalPage] = useState(page);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(rowsPerPage);

  const handleSelectAll = (checked) => {
    if (onSelectAll) {
      onSelectAll(checked);
    }
  };

  const handleSelectOne = (checked, id) => {
    if (onSelectOne) {
      onSelectOne(checked, id);
    }
  };

  const handlePageChange = (newPage) => {
    setLocalPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setLocalRowsPerPage(newRowsPerPage);
    setLocalPage(0);
    if (onRowsPerPageChange) {
      onRowsPerPageChange(newRowsPerPage);
    }
  };

  // Calculate pagination values
  const totalPages = Math.ceil(totalCount / localRowsPerPage);
  const showPagination = totalCount > localRowsPerPage;
  
  const pageItems = [];
  const maxVisiblePages = 5;
  
  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pageItems.push(i);
    }
  } else {
    // Always show first page
    pageItems.push(0);
    
    // Calculate start and end page numbers
    let startPage = Math.max(localPage - 1, 1);
    let endPage = Math.min(startPage + maxVisiblePages - 3, totalPages - 2);
    
    if (endPage - startPage < maxVisiblePages - 3) {
      startPage = Math.max(endPage - (maxVisiblePages - 3) + 1, 1);
    }
    
    // Add ellipsis if needed
    if (startPage > 1) {
      pageItems.push(-1); // -1 represents ellipsis
    }
    
    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
      pageItems.push(i);
    }
    
    // Add ellipsis if needed
    if (endPage < totalPages - 2) {
      pageItems.push(-2); // -2 represents ellipsis
    }
    
    // Always show last page
    pageItems.push(totalPages - 1);
  }
  
  return (
    <div className="rounded-lg overflow-hidden bg-white">
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
            <div className="text-primary animate-spin w-6 h-6 border-2 border-current border-t-transparent rounded-full" 
                 aria-label="Carregando..."></div>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableHeaderRow className="bg-[#ffa300] text-white">
              {selectable && (
                <TableHead className="w-[40px] text-white">
                  <Checkbox
                    checked={data.length > 0 && selected.length === data.length}
                    onCheckedChange={handleSelectAll}
                    aria-label="Selecionar todos"
                  />
                </TableHead>
              )}
              {columns.map((column) => (
                <TableHead 
                  key={column.field}
                  className={cn("font-medium text-white", column.className)}
                  style={{ width: column.width }}
                >
                  {column.headerName}
                </TableHead>
              ))}
              {actions && <TableHead className="text-left text-white">{t('common.actions')}</TableHead>}
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell 
                  colSpan={columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)}
                  className="text-center h-32 text-[#64748B]"
                >
                  {loading ? 'Carregando...' : 'Nenhum dado encontrado'}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => {
                const isSelected = selected?.includes(row.id);
                return (
                  <TableRow
                    key={row.id || index}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn(
                      "transition-colors",
                      onRowClick ? "cursor-pointer" : "",
                      isSelected ? "bg-primary/5" : ""
                    )}
                    data-state={isSelected ? "selected" : undefined}
                  >
                    {selectable && (
                      <TableCell className="w-[40px]">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => handleSelectOne(checked, row.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Selecionar linha ${index + 1}`}
                        />
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell key={column.field}>
                        {column.renderCell ? column.renderCell(row) : row[column.field]}
                      </TableCell>
                    ))}
                    {actions && (
                      <TableCell className="text-left">
                        <div className="flex justify-start gap-2">
                          {actions.map((action, actionIndex) => (
                            <TooltipProvider key={actionIndex}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className={cn("h-8 w-8", action.className)}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      action.onClick(row);
                                    }}
                                    aria-label={action.label || `Ação ${actionIndex + 1}`}
                                  >
                                    {action.icon}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {action.label || `Ação ${actionIndex + 1}`}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ))}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {showPagination && (
        <div className="flex items-center justify-between border-t px-4 py-3">
          <div className="text-sm text-[#64748B]">
            Mostrando {Math.min(localPage * localRowsPerPage + 1, totalCount)} a {Math.min((localPage + 1) * localRowsPerPage, totalCount)} de {totalCount} registros
          </div>
          
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => handlePageChange(Math.max(0, localPage - 1))}
                  disabled={localPage === 0}
                  aria-label="Página anterior"
                />
              </PaginationItem>
              
              {pageItems.map((pageNum, idx) => {
                if (pageNum < 0) {
                  return (
                    <PaginationItem key={`ellipsis-${pageNum}`}>
                      <div className="h-10 w-10 flex items-center justify-center">
                        <span className="text-[#64748B]">…</span>
                      </div>
                    </PaginationItem>
                  );
                }
                
                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      isActive={pageNum === localPage}
                      onClick={() => handlePageChange(pageNum)}
                      aria-label={`Página ${pageNum + 1}`}
                    >
                      {pageNum + 1}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              
              <PaginationItem>
                <PaginationNext
                  onClick={() => handlePageChange(Math.min(totalPages - 1, localPage + 1))}
                  disabled={localPage >= totalPages - 1}
                  aria-label="Próxima página"
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Linhas por página:</span>
            <select
              value={localRowsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background px-2 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label="Linhas por página"
            >
              {[5, 10, 25, 50].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
